import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import carBannerImg from '../../../assets/images/carbannerImg.jpg';
import { URLS } from '../../../constants/urls';
import { Button } from '../../components/button';
import { Alert, Card, CardBody, CardHeader, ErrorText, Label } from '../../components/ui';
import { GET_PRESIGNED_URL_FOR_BRAND_BANNER } from '../../services/brandService/queries';
import {
  HOME_BANNER_HEIGHT,
  HOME_BANNER_MAX_BYTES,
  HOME_BANNER_WIDTH,
  homeBannerFileType,
  homeBannerPublicUrl,
  readImageSize,
  validateHomeBannerImage,
} from '../../utils/homeBannerImage';
import {
  HomePageSettings,
  HomeWords,
  OVERLAY_OPACITY_MAX,
  OVERLAY_OPACITY_MIN,
  STANDARD_OVERLAY_CSS,
  heroOverlayCss,
  isHexColor,
  overlayReadabilityWarning,
} from '../../utils/homePageSettings';
import {
  HomePagePatch,
  OverlayForm,
  buildOverlayPatch,
  canSaveOverlay,
  initialOverlayForm,
  overlayErrors,
} from './pageSettingsForm';

const Preview = styled.div<{ $image: string }>`
  ${tw`w-full overflow-hidden mt-4 relative flex flex-col items-center justify-center text-center`}
  aspect-ratio: ${HOME_BANNER_WIDTH} / ${HOME_BANNER_HEIGHT};
  border-radius: var(--radius-lg);
  background-color: #18181b;
  background-image: url(${({ $image }) => JSON.stringify($image)});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  border: 1px solid var(--color-border);
`;

const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
`;

const PreviewWords = styled.div`
  ${tw`relative px-4`}
  max-width: 640px;
`;

const PreviewTitle = styled.p`
  ${tw`m-0 font-display font-bold`}
  color: #fff;
  font-size: clamp(16px, 3.2vw, 34px);
  line-height: 1.15;
`;

const PreviewSubtitle = styled.p`
  ${tw`mt-2 mb-0`}
  color: rgba(255, 255, 255, 0.85);
  font-size: clamp(11px, 1.4vw, 15px);
  line-height: 1.4;
`;

const HiddenFileInput = styled.input`
  ${tw`hidden`}
`;

const SizeList = styled.ul`
  ${tw`mt-2 mb-0 pl-5`}
  li + li {
    margin-top: 4px;
  }
`;

const Actions = styled.div`
  ${tw`flex flex-wrap gap-2 mt-4`}
`;

const PreviewNote = styled.p`
  ${tw`mt-2 mb-0 text-sm`}
  color: var(--color-text-secondary);
`;

const Section = styled.div`
  ${tw`mt-6 pt-5`}
  border-top: 1px solid var(--color-border);
`;

const SectionTitle = styled.h3`
  ${tw`m-0 text-base font-semibold`}
  color: var(--color-text-primary);
`;

const Row = styled.div`
  ${tw`flex flex-wrap items-end gap-4 mt-3`}
`;

const Control = styled.div`
  ${tw`flex flex-col min-w-0`}
`;

const ColorRow = styled.div`
  ${tw`flex items-center gap-2`}
`;

const Swatch = styled.input`
  width: 40px;
  height: 40px;
  padding: 2px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-elevated, #fff);
  cursor: pointer;
`;

const HexInput = styled.input`
  ${tw`text-sm`}
  width: 110px;
  height: 40px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  text-transform: uppercase;
`;

const Slider = styled.input`
  width: 220px;
  max-width: 100%;
  accent-color: var(--color-black);
`;

const Percent = styled.span`
  ${tw`text-sm font-medium ml-2`}
  font-variant-numeric: tabular-nums;
`;

const ErrorWrap = styled.div`
  ${tw`mt-3`}
`;

const maxMb = HOME_BANNER_MAX_BYTES / (1024 * 1024);

/**
 * Homepage banner photo (existing) plus its overlay (spec 029 US3). The
 * preview shows the saved website words on top, like the homepage.
 */
export function BannerCard({
  bannerKey,
  saved,
  words,
  showOverlay,
  savingOverlay,
  overlayServerErrors,
  onSaveBannerKey,
  onSaveOverlay,
}: {
  bannerKey: string | null | undefined;
  saved: HomePageSettings;
  words: HomeWords;
  showOverlay: boolean;
  savingOverlay: boolean;
  overlayServerErrors: Record<string, string> | null;
  onSaveBannerKey: (key: string | null, success: string) => Promise<void>;
  onSaveOverlay: (patch: HomePagePatch) => Promise<void>;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL_FOR_BRAND_BANNER);
  const [uploading, setUploading] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<OverlayForm>(() => initialOverlayForm(saved));
  const [hexText, setHexText] = useState(overlay.color);

  const savedOverlayKey = `${saved.overlayColor}|${saved.overlayOpacity}`;
  useEffect(() => {
    const next = initialOverlayForm(saved);
    setOverlay(next);
    setHexText(next.color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedOverlayKey]);

  const customUrl = homeBannerPublicUrl(bannerKey, URLS.S3_BUCKET.CAR_THUMBNAILS);
  const previewUrl = customUrl || carBannerImg;
  const busy = uploading || savingKey;
  const overlayCss = !showOverlay
    ? heroOverlayCss(saved.overlayColor, saved.overlayOpacity)
    : overlay.standard
      ? STANDARD_OVERLAY_CSS
      : heroOverlayCss(isHexColor(overlay.color) ? overlay.color : null, overlay.opacity);
  const errors = overlayErrors(overlay);
  const warn = !overlay.standard && overlayReadabilityWarning(overlay.color, overlay.opacity);

  const saveKey = async (key: string | null, success: string) => {
    setSavingKey(true);
    try {
      await onSaveBannerKey(key, success);
    } finally {
      setSavingKey(false);
    }
  };

  const handleFile = async (file: File | undefined) => {
    setFileError(null);
    if (!file) return;
    try {
      const type = homeBannerFileType(file);
      const { width, height } = await readImageSize(file);
      const problem = validateHomeBannerImage({ width, height, bytes: file.size, type });
      if (problem) {
        setFileError(problem);
        return;
      }
      setUploading(true);
      const presign = await getPresignedUrl({
        variables: { fileType: type, contentLength: file.size },
      });
      const upload = presign.data?.getPresignedUrlForBrandBanner;
      if (!upload?.url || !upload?.key) {
        throw new Error('Could not start the upload');
      }
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload.url);
        xhr.setRequestHeader('Content-Type', type);
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () =>
          reject(new Error('Upload failed. Check your connection and try again.'));
        xhr.send(file);
      });
      await saveKey(upload.key, 'Homepage banner updated');
    } catch (err: any) {
      const message = err?.message || 'Could not update the homepage banner';
      setFileError(message);
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleReset = async () => {
    setFileError(null);
    try {
      await saveKey(null, 'Homepage banner reset to the standard photo');
    } catch (err: any) {
      toast.error(err?.message || 'Could not reset the homepage banner');
    }
  };

  const setColor = (value: string) => {
    setHexText(value);
    const v = value.trim();
    const hex = v.startsWith('#') ? v : `#${v}`;
    setOverlay(prev => ({ ...prev, standard: false, color: hex }));
  };

  return (
    <Card data-testid="page-settings-banner">
      <CardHeader
        title="Homepage banner"
        description="Guests see this photo behind the search on the homepage. Leave it unset to keep the standard photo."
      />
      <CardBody>
        <Alert
          tone="info"
          title={`Required size: ${HOME_BANNER_WIDTH} × ${HOME_BANNER_HEIGHT} pixels`}
        >
          <SizeList>
            <li>
              Use{' '}
              <strong>
                {HOME_BANNER_WIDTH} × {HOME_BANNER_HEIGHT} px
              </strong>
              . That is the standard banner. A larger file with the same wide shape is fine, up to
              4096 px wide.
            </li>
            <li>JPEG, PNG, or WebP. {maxMb} MB or smaller.</li>
            <li>
              The homepage fills the frame and crops the edges, so keep the car near the center. A
              tinted overlay stays on top so the headline stays readable.
            </li>
          </SizeList>
        </Alert>

        <Preview
          $image={previewUrl}
          role="img"
          aria-label={customUrl ? 'Your homepage banner' : 'Standard homepage banner'}
          data-testid="banner-preview"
        >
          <PreviewOverlay style={{ background: overlayCss }} data-testid="banner-preview-overlay" />
          <PreviewWords>
            <PreviewTitle>{words.webTitle}</PreviewTitle>
            <PreviewSubtitle>{words.webSubtitle}</PreviewSubtitle>
          </PreviewWords>
        </Preview>
        <PreviewNote>
          {customUrl
            ? 'Guests on this brand see your photo.'
            : 'No upload yet. Guests see the standard photo.'}
        </PreviewNote>

        {fileError && (
          <ErrorWrap>
            <Alert tone="error" title="This image cannot be used">
              {fileError}
            </Alert>
          </ErrorWrap>
        )}

        <Actions>
          <Button
            text={uploading ? 'Uploading…' : 'Upload banner'}
            type="button"
            onClick={() => fileInputRef.current?.click()}
            isLoading={uploading}
            isDisabled={busy}
          />
          {customUrl && (
            <Button
              text={savingKey && !uploading ? 'Saving…' : 'Use the standard photo'}
              type="button"
              theme="outlined"
              onClick={handleReset}
              isLoading={savingKey && !uploading}
              isDisabled={busy}
            />
          )}
        </Actions>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
          onChange={event => handleFile(event.target.files?.[0])}
        />

        {showOverlay && (
          <Section data-testid="banner-overlay">
            <SectionTitle>Banner overlay</SectionTitle>
            <PreviewNote>
              {overlay.standard
                ? 'Standard overlay. Pick a color or strength to change it.'
                : 'The tint over the photo. It is strongest in the top left corner.'}
            </PreviewNote>
            <Row>
              <Control>
                <Label htmlFor="overlay-color">Color</Label>
                <ColorRow>
                  <Swatch
                    type="color"
                    aria-label="Overlay color"
                    value={isHexColor(overlay.color) ? overlay.color.toLowerCase() : '#18181b'}
                    disabled={savingOverlay}
                    onChange={e => setColor(e.target.value)}
                  />
                  <HexInput
                    id="overlay-color"
                    data-testid="overlay-color"
                    value={hexText}
                    maxLength={7}
                    disabled={savingOverlay}
                    onChange={e => setColor(e.target.value)}
                  />
                </ColorRow>
              </Control>
              <Control>
                <Label htmlFor="overlay-opacity">Strength</Label>
                <ColorRow>
                  <Slider
                    id="overlay-opacity"
                    data-testid="overlay-opacity"
                    type="range"
                    min={OVERLAY_OPACITY_MIN}
                    max={OVERLAY_OPACITY_MAX}
                    step={5}
                    value={overlay.opacity}
                    disabled={savingOverlay}
                    onChange={e =>
                      setOverlay(prev => ({
                        ...prev,
                        standard: false,
                        opacity: Number(e.target.value),
                      }))
                    }
                  />
                  <Percent>{overlay.opacity}%</Percent>
                </ColorRow>
              </Control>
            </Row>
            {(errors.color || overlayServerErrors?.overlayColor) && (
              <ErrorText role="alert">
                {errors.color ?? overlayServerErrors?.overlayColor}
              </ErrorText>
            )}
            {(errors.opacity || overlayServerErrors?.overlayOpacity) && (
              <ErrorText role="alert">
                {errors.opacity ?? overlayServerErrors?.overlayOpacity}
              </ErrorText>
            )}
            {warn && (
              <ErrorWrap>
                <Alert tone="warning" title="White text may be hard to read on this overlay.">
                  Choose a darker color or a stronger overlay, and check the preview.
                </Alert>
              </ErrorWrap>
            )}
            <Actions>
              <Button
                text={savingOverlay ? 'Saving…' : 'Save overlay'}
                type="button"
                onClick={() => onSaveOverlay(buildOverlayPatch(overlay, saved))}
                isLoading={savingOverlay}
                isDisabled={savingOverlay || !canSaveOverlay(overlay, saved)}
                dataTestId="overlay-save"
              />
              {!overlay.standard && (
                <Button
                  text="Use the standard overlay"
                  type="button"
                  theme="outlined"
                  onClick={() => {
                    const next = {
                      ...initialOverlayForm({ ...saved, overlayColor: null, overlayOpacity: null }),
                    };
                    setOverlay(next);
                    setHexText(next.color);
                  }}
                  isDisabled={savingOverlay}
                  dataTestId="overlay-standard"
                />
              )}
            </Actions>
          </Section>
        )}
      </CardBody>
    </Card>
  );
}
